import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "@hassanmojab/react-modern-calendar-datepicker";
import moment from "jalali-moment";
import {
  CalendarDays, X, Check, RotateCcw, ChevronDown, Sparkles,
} from "lucide-react";

/* ================================================================
 *  CalendarInput — با Portal
 * ================================================================ */
const CalendarInput = ({ label, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef();
  const popupRef = useRef();
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 0 });

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const compute = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const POPUP_WIDTH = Math.min(320, window.innerWidth - 24);

      let left = rect.left + rect.width / 2 - POPUP_WIDTH / 2;
      if (left < 12) left = 12;
      if (left + POPUP_WIDTH > window.innerWidth - 12) {
        left = window.innerWidth - POPUP_WIDTH - 12;
      }

      setPopupPos({
        top: rect.bottom + 8,
        left,
        width: POPUP_WIDTH,
      });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current?.contains(event.target) ||
        popupRef.current?.contains(event.target)
      ) return;
      setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const formatDate = (date) => {
    if (!date) return "";
    return moment
      .from(`${date.year}/${date.month}/${date.day}`, "fa", "YYYY/MM/DD")
      .locale("fa")
      .format("jYYYY/jMM/jDD");
  };

  return (
    <div className="relative w-full">
      <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 dark:!text-slate-400 mb-1.5 tracking-wide">
        <span className="h-1 w-1 rounded-full bg-amber-400" />
        {label}
      </label>

      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`
          group relative overflow-hidden
          flex items-center justify-between gap-2 w-full
          px-3 py-2.5 rounded-xl text-sm
          transition-all duration-300
          ${isOpen
            ? "bg-gradient-to-l from-amber-300/15 to-amber-400/5 border-amber-300/60 text-slate-800 dark:!text-amber-100 ring-2 ring-amber-300/30"
            : value
              ? "bg-gradient-to-l from-teal-500/10 to-amber-300/5 border-teal-500/40 text-slate-800 dark:!text-amber-100 hover:border-amber-300/60"
              : "bg-white/70 dark:!bg-slate-800/60 border-teal-500/20 dark:!border-teal-400/20 text-slate-700 dark:!text-slate-200 hover:border-amber-300/60 hover:bg-amber-300/5"
          }
          border
        `}
      >
        <span className="relative flex items-center gap-2 min-w-0">
          <span
            className={`
              shrink-0 flex h-7 w-7 items-center justify-center rounded-lg
              transition-all duration-300
              ${value
                ? "bg-gradient-to-br from-amber-300/40 to-amber-500/20 border border-amber-300/50"
                : "bg-gradient-to-br from-teal-300/30 to-teal-500/10 border border-teal-400/40"
              }
              group-hover:scale-110 group-hover:rotate-3
            `}
          >
            <CalendarDays
              size={13}
              className={value ? "text-amber-600 dark:!text-amber-300" : "text-teal-600/80 dark:!text-teal-300/80"}
            />
          </span>

          <span
            className={`
              truncate tabular-nums text-[12px] sm:text-[13px]
              ${!value ? "text-slate-400 dark:!text-slate-500" : "font-bold text-slate-800 dark:!text-amber-100"}
            `}
          >
            {value ? formatDate(value) : "انتخاب تاریخ"}
          </span>
        </span>

        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="shrink-0 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/15 transition-all duration-200"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {isOpen && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: "fixed",
              top: popupPos.top,
              left: popupPos.left,
              width: popupPos.width,
              zIndex: 9999,
            }}
            className="
              rounded-2xl
              bg-white dark:!bg-slate-900
              border border-teal-500/25 dark:!border-teal-400/20
              shadow-[0_20px_60px_-15px_rgba(15,118,110,0.5)]
              dark:!shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)]
            "
          >
            <div className="relative overflow-hidden rounded-2xl">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />
            </div>

            <div className="relative">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-teal-500/10 dark:!border-teal-400/10">
                <span className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 dark:!text-slate-400">
                  <Sparkles size={10} className="text-amber-500" />
                  انتخاب {label}
                </span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                >
                  <X size={12} />
                </button>
              </div>

              <div className="p-2">
                <Calendar
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                    setIsOpen(false);
                  }}
                  locale="fa"
                  calendarClassName="custom-calendar"
                  colorPrimary="#14B8A6"
                  shouldHighlightWeekends
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/* ================================================================
 *  DateFilterBox — پاپ‌آپ همیشه زیر دکمه
 * ================================================================ */
const DateFilterBox = ({
  selectedMinDate,
  selectedMaxDate,
  setSelectedMinDate,
  setSelectedMaxDate,
  onClearFilters,
}) => {
  const [isBoxOpen, setIsBoxOpen] = useState(false);
  const buttonRef = useRef();
  const popupRef = useRef();
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 340 });

  /* ---------- محاسبه موقعیت: همیشه زیر دکمه ---------- */
  useLayoutEffect(() => {
    if (!isBoxOpen || !buttonRef.current) return;

    const compute = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const POPUP_WIDTH = Math.min(340, window.innerWidth - 24);

      // تراز از سمت راست دکمه (RTL) - اگه از لبه چپ بیرون زد، جبران کن
      let left = rect.right - POPUP_WIDTH;
      if (left < 12) left = 12;
      if (left + POPUP_WIDTH > window.innerWidth - 12) {
        left = window.innerWidth - POPUP_WIDTH - 12;
      }

      setPopupPos({
        top: rect.bottom + 12,   // ← همیشه زیر دکمه با فاصله ۱۲px
        left,
        width: POPUP_WIDTH,
      });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [isBoxOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        buttonRef.current?.contains(e.target) ||
        popupRef.current?.contains(e.target)
      ) return;
      setIsBoxOpen(false);
    };
    if (isBoxOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isBoxOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsBoxOpen(false);
    };
    if (isBoxOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isBoxOpen]);

  const hasDateFilter = selectedMinDate || selectedMaxDate;

  const handleClear = () => {
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
    if (onClearFilters) onClearFilters();
    setIsBoxOpen(false);
  };

  const formatDate = (date) => {
    if (!date) return "";
    return moment
      .from(`${date.year}/${date.month}/${date.day}`, "fa", "YYYY/MM/DD")
      .locale("fa")
      .format("jYYYY/jMM/jDD");
  };

  return (
    <div className="relative inline-block">
      {/* ================= Trigger ================= */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsBoxOpen((prev) => !prev)}
        aria-expanded={isBoxOpen}
        className={`
          group relative overflow-hidden
          flex items-center gap-2
          h-10 px-3 sm:px-4 rounded-xl
          text-xs sm:text-sm font-bold
          transition-all duration-300
          whitespace-nowrap
          hover:scale-[1.03] active:scale-95
          ${isBoxOpen
            ? "text-amber-900 dark:!text-amber-100 bg-gradient-to-l from-amber-300/80 via-amber-400/60 to-amber-300/40 border border-amber-300/70 shadow-[0_6px_24px_-6px_rgba(251,191,36,0.8)]"
            : hasDateFilter
              ? "text-amber-800 dark:!text-amber-200 bg-gradient-to-l from-amber-300/40 via-amber-300/20 to-amber-400/10 border border-amber-300/60 hover:from-amber-300/60 hover:via-amber-400/40 hover:to-amber-300/30"
              : "text-teal-700 dark:!text-amber-200 bg-white/70 dark:!bg-white/5 border border-teal-500/25 dark:!border-teal-400/20 backdrop-blur-md hover:border-amber-300/60 hover:bg-amber-300/10"
          }
        `}
      >
        <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <span
          className={`
            relative shrink-0 flex h-6 w-6 items-center justify-center rounded-lg
            transition-all duration-300
            ${hasDateFilter
              ? "bg-white/50 dark:!bg-white/10 border border-amber-400/40"
              : "bg-gradient-to-br from-teal-300/40 to-teal-500/20 border border-teal-400/40"
            }
            group-hover:scale-110 group-hover:rotate-3
          `}
        >
          <CalendarDays
            size={13}
            className={hasDateFilter ? "text-amber-700 dark:!text-amber-300" : "text-teal-700 dark:!text-teal-300"}
          />
        </span>

        <span className="relative hidden sm:inline">
          {hasDateFilter ? "بازه تاریخ" : "فیلتر تاریخ"}
        </span>
        <span className="relative sm:hidden">تاریخ</span>

        <ChevronDown
          size={13}
          className={`relative shrink-0 transition-transform duration-300 ${isBoxOpen ? "rotate-180" : ""}`}
        />

        {hasDateFilter && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400 border-2 border-white dark:!border-slate-900" />
          </span>
        )}
      </button>

      {/* ================= Popup via Portal — همیشه زیر دکمه ================= */}
      {isBoxOpen && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: "fixed",
              top: popupPos.top,
              left: popupPos.left,
              width: popupPos.width,
              zIndex: 9998,
            }}
            className="
              rounded-2xl
              bg-white dark:!bg-slate-900
              border border-teal-500/25 dark:!border-teal-400/20
              shadow-[0_25px_70px_-20px_rgba(15,118,110,0.6),0_0_40px_-15px_rgba(251,191,36,0.35)]
              dark:!shadow-[0_25px_70px_-20px_rgba(0,0,0,0.9)]
            "
          >
            {/* تزئینات */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />
              <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-amber-300/20 blur-3xl" />
              <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-teal-400/15 blur-3xl" />
            </div>

            <div className="relative">
              {/* Header */}
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-teal-500/10 dark:!border-teal-400/10">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/50 to-teal-400/25 border border-amber-300/50">
                    <Sparkles size={14} className="text-amber-500" />
                  </span>
                  <div className="min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-800 dark:!text-amber-100 truncate">
                      فیلتر تاریخ
                    </h4>
                    <p className="text-[10px] text-slate-500 dark:!text-slate-400 mt-0.5 truncate">
                      بازه زمانی مورد نظر را انتخاب کنید
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setIsBoxOpen(false)}
                  className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-all duration-200"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Selected range preview */}
              {hasDateFilter && (
                <div className="mx-4 mt-3 rounded-xl bg-gradient-to-l from-teal-500/10 via-transparent to-amber-300/10 border border-teal-500/20 px-3 py-2 flex items-center gap-2">
                  <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-gradient-to-br from-amber-300 to-teal-400" />
                  <div className="min-w-0 flex-1 flex items-center gap-1.5 text-[11px] font-bold text-slate-700 dark:!text-slate-200 tabular-nums">
                    {selectedMinDate && <span className="truncate">{formatDate(selectedMinDate)}</span>}
                    {selectedMinDate && selectedMaxDate && <span className="text-slate-400">←</span>}
                    {selectedMaxDate && <span className="truncate">{formatDate(selectedMaxDate)}</span>}
                    {!selectedMinDate && <span className="text-slate-400">از ابتدا</span>}
                    {!selectedMaxDate && <span className="text-slate-400">تا امروز</span>}
                  </div>
                </div>
              )}

              {/* Body */}
              <div className="flex flex-col gap-3 p-4">
                <CalendarInput
                  label="از تاریخ"
                  value={selectedMinDate}
                  onChange={setSelectedMinDate}
                />
                <CalendarInput
                  label="تا تاریخ"
                  value={selectedMaxDate}
                  onChange={setSelectedMaxDate}
                />

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-teal-500/10 dark:!border-teal-400/10">
                  <button
                    type="button"
                    onClick={() => setIsBoxOpen(false)}
                    className="
                      group/apply relative overflow-hidden
                      flex-1 flex items-center justify-center gap-1.5
                      py-2.5 px-3 rounded-xl
                      text-xs sm:text-sm font-bold text-white
                      bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                      shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                      hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                      hover:scale-[1.02] active:scale-95
                      transition-all duration-300
                    "
                  >
                    <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover/apply:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                    <Check size={14} className="relative" />
                    <span className="relative">اعمال</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={!hasDateFilter}
                    className={`
                      flex items-center justify-center gap-1.5
                      px-3.5 py-2.5 rounded-xl
                      text-xs font-bold
                      transition-all duration-300
                      whitespace-nowrap
                      ${hasDateFilter
                        ? "text-rose-600 dark:!text-rose-300 border border-rose-400/40 bg-rose-500/5 hover:bg-rose-500/15 hover:scale-[1.03] active:scale-95"
                        : "text-slate-400 dark:!text-slate-600 bg-slate-100/50 dark:!bg-slate-800/40 border border-slate-200/50 dark:!border-slate-700/50 cursor-not-allowed"
                      }
                    `}
                  >
                    <RotateCcw size={13} />
                    <span className="hidden sm:inline">حذف</span>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DateFilterBox;